import { useInjectReducer } from "@app/store";
import PageContent from "@components/PageContent";
import PageHeader from "@components/PageHeader";
import Table from "@components/Table";
import shippingChargeSlice, {
  clearOne,
  setQueryValue,
} from "@features/admin/shipping/shippingCharge/shippingChargeSlice";
import { makeSelectQuery } from "@features/admin/shipping/shippingCharge/shippingChargeSelector";
import { Helmet } from "react-helmet";
import { FaPencilAlt, FaPlus, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useDeleteShippingChargeMutation,
  useLoadAllShippingChargeMutation,
} from "@features/admin/shipping/shippingCharge/shippingChargeApiSlice";
import { useEffect, useState } from "react";
import { queryHelper } from "@utils/queryHelper";
import DeleteDialog from "@components/DeleteDialog";
import { enqueueSnackbar } from "@features/auth/authSlice";
import PageFilter from "@components/PageFilter";
import { FilterInput, FilterSelect } from "@components/FilterInputs";
import { useMemo } from "react";
import { useLoadSellerQuery } from "@features/admin/productsManage/productsApiSlice";

export const ShippingCharge = () => {
  useInjectReducer("shippingCharge", shippingChargeSlice);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openMultiple, setMultipleOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [queryObj, setQueryObj] = useState({});

  const query = useSelector(makeSelectQuery);

  const [
    allShippingCharge,
    { data: allShippingChargeData, isLoading: loading, isSuccess: success },
  ] = useLoadAllShippingChargeMutation();

  const [deleteShippingCharge] = useDeleteShippingChargeMutation();

  const { data: sellerInfo, isLoading: sellerLoading } = useLoadSellerQuery();
  const sellerType = sellerInfo?.data ?? [];

  const { sellerTypeOptions } = useMemo(() => {
    let sellerTypeOptions = [];
    if (!sellerLoading && sellerType) {
      sellerTypeOptions = sellerType?.map((each) => ({
        label: each?.company?.company_name,
        value: each._id,
      }));
    }
    return { sellerTypeOptions };
  }, [sellerLoading]);

  useEffect(() => {
    allShippingCharge(queryHelper(query));
  }, []);

  const handleOpen = (id) => {
    setDeleteId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAdd = () => {
    dispatch(clearOne());
    navigate("/admin/shipping-charge/add");
  };

  const handleEdit = (id) => {
    navigate(`/admin/shipping-charge/edit/${id}`);
  };

  const handleChangeQueryDropdown = (name) => (event) => {
    dispatch(setQueryValue({ ...query, ...queryObj }));
    allShippingCharge(queryHelper({ ...query, ...queryObj }));
  };

  const handleDelete = () => {
    deleteShippingCharge(deleteId)
      .unwrap()
      .then((res) => {
        dispatch(
          enqueueSnackbar({
            message: res?.msg || "Shipping Charge Deleted Successfully.",
            options: {
              variant: "success",
            },
          })
        );
        allShippingCharge(queryHelper(query));
        setOpen(false);
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: err?.data?.msg || "Something Went Wrong.",
            options: {
              variant: "warning",
            },
          })
        );
      });
  };

  const tableData = success
    ? allShippingChargeData?.data?.map(
        ({
          _id,
          title,
          is_active,
          priority,
          price,
          set_by,
          free_shipping_price,
        }) => [
          <div className="mx-3 whitespace-nowrap"> {title} </div>,
          set_by,
          price,
          free_shipping_price ? free_shipping_price : 0,
          priority,
          is_active ? "Active" : "Not Active",
          <>
            <div className="flex justify-center">
              {/* check location access */}
              <button
                type="button"
                aria-label="Edit"
                className="w-6 inline-flex justify-center leading-none cursor-pointer relative"
                onClick={() => handleEdit(_id)}
              >
                <FaPencilAlt className="tblpencil text-xs text-blue-400 hover:text-blue-500" />
              </button>
              {/* check location access */}
              <button
                type="button"
                className="w-6 inline-flex justify-center items-center leading-none cursor-pointer relative"
                onClick={() => handleOpen(_id)}
              >
                <FaTrash className="tbltrash text-xs text-red-400 hover:text-red-500" />
              </button>
            </div>
          </>,
        ]
      )
    : [];

  const handlePagination = ({ page, size }) => {
    allShippingCharge(queryHelper(query));
    dispatch(setQueryValue({ key: "page", value: page }));
    dispatch(setQueryValue({ key: "size", value: size }));
  };

  const tablePagination = {
    page: allShippingChargeData?.page ?? 1,
    size: allShippingChargeData?.size ?? 10,
    totalData: allShippingChargeData?.totalData ?? 0,
  };

  return (
    <>
      <DeleteDialog
        open={open}
        doClose={handleClose}
        doDelete={() => handleDelete(deleteId)}
      />
      <Helmet>
        <title> Shipping Charge </title>
      </Helmet>
      <div className="flex justify-between mt-3 mb-3" id="page-header">
        <PageHeader title="Shipping Charge" />
        <div className="flex items-center">
          <button className="btn btn-admin bg-success" onClick={handleAdd}>
            <div className="flex items-center">
              <FaPlus />
              <span className="pl-2"> Add New </span>
            </div>
          </button>
        </div>
      </div>
      <PageFilter>
        <div className="w-1/6">
          <FilterSelect
            label=" Search By Seller"
            fieldsetId="find_by_seller"
            options={[{ label: "All", value: "" }, ...sellerTypeOptions]}
            onChange={handleChangeQueryDropdown("find_by_seller")}
          />
        </div>
      </PageFilter>
      <PageContent loading={loading}>
        <Table
          tableHead={[
            "Title",
            "Set By",
            "Price",
            "Free Shipping Price",
            "Priority",
            "Status",
            <div className="text-center">Action</div>,
          ]}
          tableData={tableData}
          pagination={tablePagination}
          handlePagination={handlePagination}
          emptyDataMsg="No Shipping Partners Found"
          loading={loading}
          ispublic
          isSN
        />
      </PageContent>
    </>
  );
};

export default ShippingCharge;
